import { useRef, useEffect, forwardRef } from 'react';
import { VIDEO_AUTOPLAY_OBSERVER_OPTIONS } from '@/core/config/intersectionObserver';
import { AnalyticsVideo } from '@/shared/features/analytics/presentation/components';

interface VideoSectionPlayerProps {
  videoUrl: string;
  posterUrl?: string;
  blankImageUrl?: string;
  title: string;
  controls: boolean;
  autoplay: boolean;
  playsinline: boolean;
  loop: boolean;
  muted: boolean;
  lazyload: boolean;
  className?: string;
  shouldRefresh?: boolean;
}

const VideoSectionPlayer = forwardRef<HTMLVideoElement, VideoSectionPlayerProps>(
  (
    {
      videoUrl,
      posterUrl,
      blankImageUrl,
      title,
      controls,
      autoplay,
      playsinline,
      loop,
      muted,
      lazyload,
      className,
      shouldRefresh: _shouldRefresh,
    },
    ref
  ) => (
    <AnalyticsVideo
      ref={ref}
      className={className}
      poster={posterUrl || blankImageUrl}
      controls={controls}
      autoPlay={autoplay}
      playsInline={playsinline}
      loop={loop}
      muted={muted}
      preload={lazyload ? 'none' : 'auto'}
      videoName={title}
    >
      <source src={videoUrl} type="video/mp4" />
      Your browser does not support the video tag.
    </AnalyticsVideo>
  )
);

VideoSectionPlayer.displayName = 'VideoSectionPlayer';

interface VideoSectionProps {
  data: Record<string, unknown> & {
    title?: string[];
    videoUrl?: string;
    posterUrl?: string;
    textImgUrl?: string;
    controls?: string;
    autoplay?: string;
    playsinline?: string;
    loop?: string;
    muted?: string;
    lazyload?: string;
    blankImageUrl?: string;
    showTitle?: boolean;
    shouldRefresh?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function VideoSection({
  data,
  sectionKey: _sectionKey,
  eventLabel: _eventLabel,
  priority: _priority,
}: VideoSectionProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const mountedRef = useRef(typeof window !== 'undefined');

  // Extract video properties
  const videoUrl = data.videoUrl as string | undefined;
  const posterUrl = data.posterUrl as string | undefined;
  const titleArray = data.title as string[] | undefined;
  const title = titleArray && titleArray.length > 0 ? titleArray[0] : '';
  const showTitle = data.showTitle ?? true;
  const controls = !!data.controls;
  const autoplay = !!data.autoplay;
  const playsinline = !!data.playsinline;
  const loop = !!data.loop;
  const muted = !!data.muted;
  const lazyload = data.lazyload === 'lazyload';
  const blankImageUrl = data.blankImageUrl as string | undefined;

  // Handle video loading and play/pause based on visibility
  useEffect(() => {
    if (!mountedRef.current || !videoRef.current || !videoUrl) return;

    const video = videoRef.current;

    const handleError = (e: Event) => {
      console.error('VideoSection: video element error', { videoUrl, event: e });
    };

    video.addEventListener('error', handleError);

    // Handle intersection observer for better performance (only if autoplay)
    if (autoplay) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              video.play().catch((error) => {
                console.warn('VideoSection - Video play error:', error);
              });
            } else {
              video.pause();
            }
          });
        },
        VIDEO_AUTOPLAY_OBSERVER_OPTIONS
      );

      observer.observe(video);

      return () => {
        video.removeEventListener('error', handleError);
        observer.disconnect();
      };
    }

    return () => {
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl, autoplay]);

  if (!videoUrl) {
    return null;
  }

  return (
    <section className="w-full py-4 md:py-6">
      {/* Mobile: Full width, title above video */}
      <div className="w-full md:hidden">
        {/* Title */}
        {showTitle && title && (
          <div className="container-fluid">
            <h2 className="mb-4 text-base font-normal text-center leading-normal">
              <span className="font-primary text-[#9D7792]">
                {title}
              </span>
            </h2>
          </div>
        )}
        <div className="relative w-full">
          <VideoSectionPlayer
            ref={videoRef}
            videoUrl={videoUrl}
            posterUrl={posterUrl}
            blankImageUrl={blankImageUrl}
            title={title}
            controls={controls}
            autoplay={autoplay}
            playsinline={playsinline}
            loop={loop}
            muted={muted}
            lazyload={lazyload}
            className="w-full h-auto object-contain"
            shouldRefresh={data.shouldRefresh}
          />
        </div>
      </div>

      {/* Desktop: Video on left, title on right with divider */}
      <div className="hidden md:flex md:items-center container-fluid">
        {/* Video Container - Left Side */}
        <div className="flex-1 relative">
          <div className="border border-4 rounded-[8px] border-[#8D0000] aspect-[2/1] overflow-hidden">
            <VideoSectionPlayer
              videoUrl={videoUrl}
              posterUrl={posterUrl}
              blankImageUrl={blankImageUrl}
              title={title}
              controls={controls}
              autoplay={autoplay}
              playsinline={playsinline}
              loop={loop}
              muted={muted}
              lazyload={lazyload}
              className="w-full h-full object-cover rounded-[4px]"
            />
          </div>
        </div>

        {/* Title Container - Right Side */}
        {showTitle && title && (
          <div className="flex-1 flex items-center">
            
            <h2 className="text-base text-center font-normal leading-normal w-full">
              <span 
                className="font-primary text-[#8D0000] block border-b-2 border-[#8D0000]"
                style={{
                  fontSize: 'clamp(1.5rem, 3vw, 3.5rem)',
                  lineHeight: '1.2'
                }}
              >
                {title}
              </span>
            </h2>
          </div>
        )}
      </div>
    </section>
  );
}

